/**
 * Base API Client for Disha AI
 * Handles authentication, error parsing, and consistent fetch patterns.
 */

export interface ApiError extends Error {
  code?: string;
  status?: number;
}

function getAuthToken(): string | null {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    return params.get("token");
  }
  return null;
}

function getTargetOrigin(): string {
  if (typeof window === "undefined") return "*";

  const params = new URLSearchParams(window.location.search);
  const originParam = params.get("origin");
  if (originParam) return originParam;

  try {
    if (document.referrer) {
      return new URL(document.referrer).origin;
    }
  } catch (e) {
    // ignore
  }

  return window.location.origin;
}

/**
 * Safely broadcast events to both Web (iframes) and Native Mobile (iOS/Android)
 */
export function broadcastEvent(payload: any) {
  if (typeof window === "undefined") return;

  const targetOrigin = getTargetOrigin();

  // Web Support (iframe -> parent)
  if (window.parent && window !== window.parent) {
    window.parent.postMessage(payload, targetOrigin);
  }

  // iOS Native Support (WKWebView)
  // @ts-ignore
  if (window.webkit?.messageHandlers?.dishaHandler) {
    // @ts-ignore
    window.webkit.messageHandlers.dishaHandler.postMessage(payload);
  }

  // Android Native Support (Android WebView)
  // @ts-ignore
  if (window.dishaAndroidHandler && typeof window.dishaAndroidHandler.postMessage === 'function') {
    // @ts-ignore
    window.dishaAndroidHandler.postMessage(JSON.stringify(payload));
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Handle 401 specifically for student token expiration
    if (response.status === 401 && !response.url.includes("/api/admin/")) {
      broadcastEvent({
        source: "disha-ai",
        event: "DISHA_TOKEN_EXPIRED",
      });
      const error: ApiError = new Error("Secure chat token expired. Please refresh.");
      error.status = 401;
      throw error;
    }

    let errorData: any;
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: "An unexpected error occurred" };
    }

    const error: ApiError = new Error(errorData.error || "Request failed");
    error.code = errorData.code;
    error.status = response.status;
    throw error;
  }

  return response.json();
}

/**
 * Base request function
 */
async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const isInternalAdmin = path.includes("/api/admin/");
  const headers = new Headers(options.headers);

  if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // Admin uses cookies (credentials: include), Student uses Bearer token
  const requestOptions: RequestInit = {
    ...options,
    headers,
  };

  if (isInternalAdmin) {
    requestOptions.credentials = "include";
  } else {
    const token = getAuthToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  const response = await fetch(path, requestOptions);
  return handleResponse<T>(response);
}

export const apiClient = {
  get: <T>(path: string, options?: RequestInit) =>
    request<T>(path, { ...options, method: "GET" }),
  
  post: <T>(path: string, body?: any, options?: RequestInit) =>
    request<T>(path, {
      ...options,
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),
  
  patch: <T>(path: string, body?: any, options?: RequestInit) =>
    request<T>(path, {
      ...options,
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),
  
  delete: <T>(path: string, options?: RequestInit) =>
    request<T>(path, { ...options, method: "DELETE" }),
};
